// Pure money arithmetic for the confirmation queue's screens (spec §8.2,
// §8.4). Kept separate from lib/finance.ts and tested on their own, the same
// split calendarSelection.ts uses for the calendar's pure selection math —
// a UI bug here is a wrong number on screen, not a network call, so it is
// worth being able to hit every branch without mounting anything.
//
// All amounts are minor units (cents); the caller converts major-unit form
// strings via lib/money's toMinor exactly once, same as everywhere else in
// the finance section.

export interface ExtraLine {
  kind: 'income' | 'expense' | 'withheld';
  amountMinor: number;
}

/**
 * The single-row confirmation's settle line (spec §8.2, §4's equation
 * read backwards): the bank amount is what the payment's OTHER lines and
 * the co-settled dependents leave behind for this target's own settle line.
 *
 *   settle + Σincome + Σexpense − Σwithheld + Σdependents = bank
 *   ⇒ settle = bank − Σincome − Σexpense + Σwithheld − Σdependents
 *
 * `withheld` is accepted as a separate bucket from `extras` because the
 * dialog tracks "money the bank statement already lacks" (e.g. a platform
 * fee never hit the account) separately from lines the operator typed —
 * both feed the same equation, so both are summed with the same sign here.
 */
export function computeSettlePreview(p: {
  bankMinor: number;
  extras: ExtraLine[];
  withheld: { amountMinor: number }[];
  dependents: { amountMinor: number }[];
}): { settleMinor: number } {
  const extras = p.extras
    .filter((l) => l.kind !== 'withheld')
    .reduce((s, l) => s + l.amountMinor, 0);
  const withheld =
    p.withheld.reduce((s, l) => s + l.amountMinor, 0) +
    p.extras.filter((l) => l.kind === 'withheld').reduce((s, l) => s + l.amountMinor, 0);
  const deps = p.dependents.reduce((s, d) => s + d.amountMinor, 0);
  return { settleMinor: p.bankMinor - extras + withheld - deps };
}

/**
 * Spec §8.2: below plan needs an explicit remainder choice; at or above plan
 * never does (above plan is always `restate`, without asking — settle.go).
 * `settledSoFar` is the target's OWN prior settled_minor — a second partial
 * payment on an already-part-paid row is short against what is STILL owed,
 * not against the original full plan.
 */
export function remainderRequired(p: {
  settledMinor: number;
  planMinor: number;
  settledSoFar: number;
}): boolean {
  return p.settledMinor < p.planMinor - p.settledSoFar;
}

/**
 * The batch's own equation (spec §4, §8.4): every target's settle line plus
 * the header's extra lines, signed the same way computeSettlePreview signs
 * them, has to equal the bank amount exactly — this is what the Confirm
 * button gates on and what the on-screen equation explains.
 *
 *   Σtargets + Σincome + Σexpense − Σwithheld = bank  ⇒  diff = 0
 */
export function computeBatchEquation(p: {
  targets: number[];
  lines: ExtraLine[];
  bankMinor: number;
}): { sum: number; diff: number } {
  const targets = p.targets.reduce((s, v) => s + v, 0);
  const plus = p.lines.filter((l) => l.kind !== 'withheld').reduce((s, l) => s + l.amountMinor, 0);
  const minus = p.lines.filter((l) => l.kind === 'withheld').reduce((s, l) => s + l.amountMinor, 0);
  const sum = targets + plus - minus;
  return { sum, diff: sum - p.bankMinor };
}
